import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { baseUrl, fetchLandlordIds, getConfig } from '../../../../../serverUrl';
import { Property, CompanyPropertyResponse, PropertySettingsSummaryResponse, RentSummary } from './interface';
import { handleUnauthorized } from '../../auth401.ts';

// Helper: triggers logout and returns an RTK error shape if response is 401
function checkAuth(response: Response): { error: { status: number; data: string } } | null {
    if (response.status === 401) {
        handleUnauthorized();
        return { error: { status: 401, data: 'Unauthorized' } };
    }
    return null;
}

// Fetch landlord ID asynchronously before making API requests
const getLandlordId = async () => {
    return fetchLandlordIds();
};

type ExportArgs = {
    apartment_id?: string;
    format?: 'pdf' | 'csv';
    is_summary?: boolean;
    is_detailed?: boolean;
    is_charts?: boolean;
    month?: number;
    year?: number;
};

// Get authorization config
const getConfigData = () => getConfig();

export const propertySlice = createApi({
    reducerPath: 'propertyApi',
    baseQuery: fetchBaseQuery({ baseUrl }),
    tagTypes: ['Property'],
    endpoints: (builder) => ({
        getLandlordProperty: builder.query<Property[], void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const response = await fetch(`${baseUrl}/landlord/landlord-apartment_name/?landlord_id=${landlordId}`, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error: any) {
                    return {
                        error: {
                            status: error?.response?.status || 500,
                            data: error?.response?.data || "Failed to fetch property data",
                        },
                    };
                }
            },
            providesTags: ['Property'],
        }),
        getCompanyPropertyDetails: builder.query<CompanyPropertyResponse[], void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const response = await fetch(`${baseUrl}/landlord/company-property-details?landlord_id=${landlordId}`, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    if (!response.ok) {
                        return { error: { status: response.status, data } };
                    }
                    return { data };
                } catch (error: any) {
                    return {
                        error: {
                            status: error?.response?.status || 500,
                            data: error?.response?.data || "Failed to fetch property data",
                        },
                    };
                }
            },
            providesTags: ['Property'],
        }),

        getPropertySettingsSummary: builder.query<PropertySettingsSummaryResponse, string | undefined>({
            queryFn: async (PropertyId) => {
                try {
                    if (!PropertyId) {
                        return { error: { status: 400, data: "Property ID is required" } };
                    }

                    const response = await fetch(
                        `${baseUrl}/landlord/property-settings-summary/?property_id=${PropertyId}`,
                        getConfig()
                    );
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();

                    if (!response.ok) {
                        return { error: { status: response.status, data } };
                    }

                    return { data };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            data: error instanceof Error ? error.message : "Failed to fetch property data",
                        },
                    };
                }
            },
            providesTags: ['Property'],
        }),

        changeUnitDetails: builder.mutation({
            queryFn: async ({ houseId, bodyCredentials }) => {
                try {
                    const response = await fetch(`${baseUrl}/landlord/house/${houseId}/`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            ...getConfigData().headers,
                        },
                        body: JSON.stringify(bodyCredentials),
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch Tenant data" } };
                }
            },
            invalidatesTags: ['Property'],
        }),


        changeTenantDetailsProperty: builder.mutation({
            queryFn: async ({ tenantId, bodyCredentials }) => {
                try {
                    const response = await fetch(`${baseUrl}/tenant/tenant/${tenantId}/`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            ...getConfigData().headers,
                        },
                        body: JSON.stringify(bodyCredentials),
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch Tenant data" } };
                }
            },
            invalidatesTags: ['Property'],
        }),

        // dashboard metrics
        getRentSummaryDashboard: builder.query<RentSummary, { month: number; year: number }>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    // Destructure the month and year from the argument
                    const { month, year } = arg || {};

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/get_rent_summary/?landlord_id=${landlordId}${month ? `&month=${month}` : ''
                        }${year ? `&year=${year}` : ''}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),
        getLandlordDefaulters: builder.query<void, void>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    // Destructure the month and year from the argument

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/get_defaulters_list/?landlord_id=${landlordId}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),

        getRentPaidProgress: builder.query<void, void>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    // Destructure the month and year from the argument

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/rent_summary_per_period/?landlord_id=${landlordId}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),

        // the rent expense summary graph dashboard
        getRentExpenseSummary: builder.query<RentSummary, { month: number; year: number }>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    // Destructure the month and year from the argument
                    const { year } = arg || {};

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/react-rent-expeses-landlord/?landlord_id=${landlordId}${year ? `&year=${year}` : ''}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),


        // get the property details
        getPropertyDetails: builder.query<void, { id: number; }>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    // Destructure the id
                    const { id } = arg || {};

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/apartment/?id=${id}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),

        // get the property images
        getPropertyImage: builder.query<void, { id: number; }>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                    // Destructure the id
                    const { id } = arg || {};

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/apartment-images/?apartment_id=${id}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),

        // add the property image
        addPropertyImage: builder.mutation<void, { id: number; bodyData: FormData }>({
            queryFn: async ({ id, bodyData }) => {
                try {
                    const response = await fetch(`${baseUrl}/landlord/apartment-images/`, {
                        method: 'POST',
                        headers: {
                            ...getConfigData().headers,
                        },
                        body: bodyData,
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch apartment data" } };
                }
            },
            invalidatesTags: ['Property'],
        }),

        // delete the property image
        deletePropertyImage: builder.mutation<void, { id: number; }>({
            queryFn: async (arg) => {
                try {
                    const { id } = arg || {};

                    const response = await fetch(`${baseUrl}/landlord/apartment-image/${id}`, {
                        method: 'DELETE',
                        headers: {
                            ...getConfigData().headers,
                        },
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch apartment data" } };
                }
            },
            invalidatesTags: ['Property'],
        }),

        // get apartment defaulters
        getApartmentDefaulters: builder.query<void, { id: number; }>({
            queryFn: async (arg) => {
                try {
                    const { id } = arg || {};
                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/get_defaulters_list/?apartment_id=${id}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property defaulters" } };
                }
            },
            providesTags: ['Property'],
        }),

        // get the rent summary property
        getRentSummaryProperty: builder.query<RentSummary, { month: number; year: number; id: number }>({
            queryFn: async (arg) => {
                try {

                    // Destructure the month and year from the argument
                    const { month, year, id } = arg || {};

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/get_rent_summary/?apartment_id=${id}${month ? `&month=${month}` : ''
                        }${year ? `&year=${year}` : ''}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),

        // change the company property details
        changeCompanyDetails: builder.mutation({
            queryFn: async ({ id, companyData }) => {
                try {
                    // If companyData is FormData, do NOT set Content-Type header
                    const isFormData = companyData instanceof FormData;
                    const response = await fetch(`${baseUrl}/landlord/propety-company/${id}/`, {
                        method: 'PATCH',
                        headers: isFormData
                            ? { ...getConfigData().headers }
                            : { 'Content-Type': 'application/json', ...getConfigData().headers },
                        body: companyData,
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch Company" } };
                }
            },
            invalidatesTags: ['Property'],
        }),
        changePropertyDetails: builder.mutation({
            queryFn: async ({ id, propertyData }) => {
                try {
                    const isFormData = propertyData instanceof FormData;
                    const response = await fetch(`${baseUrl}/landlord/apartment/${id}/`, {
                        method: 'PATCH',
                        headers: isFormData
                            ? { ...getConfigData().headers }
                            : { 'Content-Type': 'application/json', ...getConfigData().headers },
                        body: isFormData ? propertyData : JSON.stringify(propertyData),
                    });
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch Company" } };
                }
            },
            invalidatesTags: ['Property'],
        }),

        getPaymentApprovalSummary: builder.query<void, void>({
            queryFn: async (arg) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    // Construct the URL with the parameters
                    const url = `${baseUrl}/landlord/payment-approval-summary/?landlord_id=${landlordId}`;

                    const response = await fetch(url, getConfigData());
                    const authError = checkAuth(response); if (authError) return authError;
                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),

        // utilities dashboard data
        getUtilitiesSummaryData: builder.query<Property[], { apartment_ids?: string | null; month?: number | null; year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    // Build query parameters
                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);

                    if (args?.apartment_ids) {
                        params.append('apartment_ids', args.apartment_ids);
                    }
                    if (args?.month) {
                        params.append('month', args.month.toString());
                    }
                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(
                        `${baseUrl}/landlord/utilities-dashboard/?${params.toString()}`,
                        getConfigData()
                    );
                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch property data" } };
                }
            },
            providesTags: ['Property'],
        }),


        getUtilitiesReportExports: builder.query<Blob | string, ExportArgs>({
            async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const landlordId = await getLandlordId();

                    if (!landlordId) {
                        return { error: { status: 500, data: 'Landlord ID not found' } };
                    }

                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);

                    if (args.apartment_id) params.append('apartment_id', args.apartment_id);
                    if (args.format) params.append('format_type', args.format);
                    if (args.is_summary) params.append('is_summary', String(args.is_summary));
                    if (args.is_detailed) params.append('is_detailed', String(args.is_detailed));
                    if (args.is_charts) params.append('is_charts', String(args.is_charts));
                    if (args.month) params.append('month', String(args.month));
                    if (args.year) params.append('year', String(args.year));

                    const url = `/landlord/utilities-dashboard-export-reports/?${params.toString()}`;

                    const result = await fetchWithBQ({
                        url,
                        method: 'GET',
                        responseHandler: async (response) => {
                            if (response.status === 401) {
                                handleUnauthorized();
                            }
                            return args.format === 'pdf' ? await response.blob() : await response.text();
                        },
                        cache: 'no-cache',
                    });

                    if (result.error) {
                        return { error: result.error };
                    }

                    return { data: result.data as Blob | string };
                } catch (err: any) {
                    return { error: { status: 500, data: err.message || 'Unknown error' } };
                }
            },
            providesTags: ['Property'],
        }),

        // add the property image
        addMeterReadings: builder.mutation<void, any>({
            queryFn: async (data) => {
                try {
                    const response = await fetch(
                        `${baseUrl}/landlord/variable-expense-units-consumed/`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                ...getConfigData().headers, 
                            },
                            body: JSON.stringify(data),
                        }
                    );
                    const authError = checkAuth(response); if (authError) return authError;

                    const json = await response.json();

                    if (!response.ok) {
                        return { error: { status: response.status, data: json } };
                    }

                    return { data: json };
                } catch (error) {
                    return {
                        error: { status: 500, data: "Failed to submit meter reading." },
                    };
                }
            },
            invalidatesTags: ['Property'],
        }),

        updateMeterReadings: builder.mutation<void, { id: number; data: any }>({
            queryFn: async ({ id, data }) => {
                try {
                    const response = await fetch(
                        `${baseUrl}/landlord/variable-expense-units-consumed/${id}/`,
                        {
                            method: 'PATCH',
                            headers: {
                                'Content-Type': 'application/json',
                                ...getConfigData().headers,
                            },
                            body: JSON.stringify(data),
                        }
                    );
                    const authError = checkAuth(response); if (authError) return authError;

                    const json = await response.json();

                    if (!response.ok) {
                        return { error: { status: response.status, data: json } };
                    }

                    return { data: json };
                } catch (error) {
                    return {
                        error: { status: 500, data: "Failed to update meter reading." },
                    };
                }
            },
            invalidatesTags: ['Property'],
        }),


    
        getAllUnits: builder.query<any[], void>({
            queryFn: async () => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }
                         
                    const response = await fetch(
                        `${baseUrl}/landlord/react-house-landlord-tenant/?landlord_id=${landlordId}`, 
                        getConfigData()
                    );
                    const authError = checkAuth(response); if (authError) return authError;
                    
                    if (!response.ok) {
                        const errorData = await response.json();
                        return { error: { status: response.status, data: errorData } };
                    }

                    const data = await response.json();
                    
                 
                    const units = Array.isArray(data) ? data : data.results || data.data || [];
                    
                    return { data: units };
                } catch (error) {
                    return { 
                        error: { 
                            status: 500, 
                            data: error instanceof Error ? error.message : "Failed to fetch all units" 
                        } 
                    };
                }
            },
            providesTags: ['Property'],
        }),




    }),
});

// Export hooks for usage in components
export const { useGetLandlordPropertyQuery, useGetCompanyPropertyDetailsQuery, useGetPropertySettingsSummaryQuery,
    useChangeUnitDetailsMutation, useChangeTenantDetailsPropertyMutation, useGetRentSummaryDashboardQuery,
    useGetLandlordDefaultersQuery, useGetRentPaidProgressQuery, useGetRentExpenseSummaryQuery, useGetPropertyDetailsQuery,
    useGetPropertyImageQuery, useAddPropertyImageMutation, useDeletePropertyImageMutation, useGetApartmentDefaultersQuery,
    useGetRentSummaryPropertyQuery, useChangeCompanyDetailsMutation, useChangePropertyDetailsMutation, useGetPaymentApprovalSummaryQuery,
    useGetUtilitiesSummaryDataQuery, useLazyGetUtilitiesReportExportsQuery, useAddMeterReadingsMutation, useUpdateMeterReadingsMutation, useGetAllUnitsQuery } = propertySlice;

